import React, { useState, useEffect } from 'react';
import { monthlyTimesheetAPI } from '../services/api';
import { MonthlyTimesheetDetail } from '../types';
import ExpandableNotes from './ExpandableNotes';

interface TimesheetDetailModalProps {
  timesheetId: number;
  onClose: () => void;
  onApprove?: (id: number) => void;
  onReject?: (id: number) => void;
  onUpdate?: () => void;
}

const TimesheetDetailModal: React.FC<TimesheetDetailModalProps> = ({
  timesheetId,
  onClose,
  onApprove,
  onReject,
  onUpdate
}) => {
  const [timesheet, setTimesheet] = useState<MonthlyTimesheetDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTimesheetDetails();
  }, [timesheetId]);

  const fetchTimesheetDetails = async () => {
    try {
      setLoading(true);
      const response = await monthlyTimesheetAPI.getById(timesheetId);
      setTimesheet(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch timesheet details:', err);
      setError('Failed to load timesheet details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse the date string manually to avoid timezone issues
    // dateString format is "YYYY-MM-DD"
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const organizeDataByDate = () => {
    if (!timesheet?.time_entries) return { dates: [], rates: [], matrix: {} };
    
    // Get unique dates and sort them
    const dateSet = new Set(timesheet.time_entries.map(e => e.date));
    const uniqueDates = Array.from(dateSet).sort();
    
    // Get unique billing rates with full info
    const rateMap = new Map<string, { id: number; name: string; project: string; fullName: string }>();
    timesheet.time_entries.forEach((entry) => {
      const key = `${entry.billing_rate}`;
      if (!rateMap.has(key)) {
        rateMap.set(key, {
          id: entry.billing_rate,
          name: entry.billing_rate_name || 'Unknown Rate',
          project: `${entry.project_code || 'Unknown'} - ${entry.project_name || 'Unknown'}`,
          fullName: `${entry.project_code || 'Unknown'} - ${entry.billing_rate_name || 'Unknown Rate'}`
        });
      }
    });
    const uniqueRates = Array.from(rateMap.values());
    
    // Build a matrix: { date: { rateId: { hours, notes } } }
    const matrix: { [date: string]: { [rateId: number]: { hours: string; notes: string } } } = {};
    timesheet.time_entries.forEach((entry) => {
      if (!matrix[entry.date]) {
        matrix[entry.date] = {};
      }
      // Sum hours if there are multiple entries for the same date/rate (shouldn't happen but just in case)
      if (matrix[entry.date][entry.billing_rate]) {
        const existingHours = parseFloat(matrix[entry.date][entry.billing_rate].hours);
        const newHours = parseFloat(entry.hours.toString());
        matrix[entry.date][entry.billing_rate].hours = (existingHours + newHours).toFixed(2);
        matrix[entry.date][entry.billing_rate].notes += entry.notes ? `\n${entry.notes}` : '';
      } else {
        matrix[entry.date][entry.billing_rate] = {
          hours: entry.hours.toString(),
          notes: entry.notes || ''
        };
      }
    });
    
    return { dates: uniqueDates, rates: uniqueRates, matrix };
  };
  
  const calculateDailyTotal = (date: string, matrix: any) => {
    if (!matrix[date]) return 0;
    return Object.values(matrix[date]).reduce((sum: number, entry: any) => {
      return sum + parseFloat(entry.hours);
    }, 0);
  };
  
  const calculateRateTotal = (rateId: number, dates: string[], matrix: any) => {
    return dates.reduce((sum, date) => {
      if (matrix[date] && matrix[date][rateId]) {
        return sum + parseFloat(matrix[date][rateId].hours);
      }
      return sum;
    }, 0);
  };

  const getWeekKey = (dateString: string) => {
    // Parse date and find the Monday of that week
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    const date = new Date(year, month - 1, day);
    
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const dayOfWeek = date.getDay();
    
    // Calculate days to subtract to get to Monday (adjust for Sunday being 0)
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // Get Monday of this week
    const monday = new Date(date);
    monday.setDate(date.getDate() - daysToMonday);
    
    // Return week key as YYYY-MM-DD of the Monday
    const weekKey = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    return weekKey;
  };

  const groupDatesByWeek = (dates: string[]) => {
    const weeks = new Map<string, string[]>();
    
    dates.forEach(date => {
      const weekKey = getWeekKey(date);
      if (!weeks.has(weekKey)) {
        weeks.set(weekKey, []);
      }
      weeks.get(weekKey)!.push(date);
    });
    
    // Sort weeks by their Monday date
    return Array.from(weeks.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const calculateWeeklyTotal = (weekDates: string[], matrix: any) => {
    return weekDates.reduce((sum, date) => {
      return sum + calculateDailyTotal(date, matrix);
    }, 0);
  };

  if (loading) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
      >
        <div 
          style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90%',
          }}
        >
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !timesheet) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div 
          style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="error">{error || 'Timesheet not found'}</div>
          <button onClick={onClose} className="btn btn-secondary" style={{ marginTop: '1rem' }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const { dates, rates, matrix } = organizeDataByDate();
  const weekGroups = groupDatesByWeek(dates);

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        overflow: 'auto',
        padding: '2rem 1rem',
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          maxWidth: '95vw',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0 }}>
          {timesheet.user_full_name || timesheet.user_username} - {' '}
          {new Date(timesheet.year, timesheet.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h2>

        {/* Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
        }}>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Status</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50' }}>
              {timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Total Hours</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50' }}>
              {parseFloat(timesheet.total_hours || '0').toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Entries</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50' }}>
              {timesheet.entries_count}
            </div>
          </div>
        </div>

        {timesheet.rejection_reason && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            borderRadius: '4px',
            color: '#c0392b'
          }}>
            <strong>Rejection Reason:</strong> {timesheet.rejection_reason}
          </div>
        )}

        {/* Time Entries by Date */}
        <div style={{ marginBottom: '2rem' }}>
          <h3>Time Entries</h3>
          {dates.length === 0 ? (
            <p style={{ color: '#7f8c8d' }}>No time entries</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.75rem', textAlign: 'left', position: 'sticky', left: 0, backgroundColor: '#2c3e50', color: 'white', zIndex: 2 }}>
                      Date
                    </th>
                    {rates.map((rate) => (
                      <th key={rate.id} style={{ 
                        padding: '0.75rem', 
                        textAlign: 'center', 
                        minWidth: '120px',
                        whiteSpace: 'normal',
                        lineHeight: '1.3',
                        backgroundColor: '#2c3e50',
                        color: 'white'
                      }}>
                        <div style={{ fontWeight: 'bold' }}>{rate.fullName}</div>
                      </th>
                    ))}
                    <th style={{ 
                      padding: '0.75rem', 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      backgroundColor: '#2c3e50',
                      color: 'white',
                      minWidth: '100px'
                    }}>
                      Daily Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {weekGroups.map(([weekKey, weekDates], weekIdx) => (
                    <React.Fragment key={weekKey}>
                      {weekDates.map((date, dateIdx) => (
                        <tr key={date} style={{ 
                          backgroundColor: dateIdx % 2 === 0 ? 'white' : '#f8f9fa',
                          borderBottom: '1px solid #e9ecef'
                        }}>
                          <td style={{ 
                            padding: '0.75rem', 
                            fontWeight: 'bold',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: dateIdx % 2 === 0 ? 'white' : '#f8f9fa',
                            zIndex: 1
                          }}>
                            {formatDate(date)}
                          </td>
                          {rates.map((rate) => {
                            const entry = matrix[date]?.[rate.id];
                            const notes = entry?.notes?.trim();
                            return (
                              <td 
                                key={rate.id} 
                                style={{ 
                                  padding: '0.75rem', 
                                  textAlign: 'center',
                                  color: entry ? '#2c3e50' : '#bdc3c7',
                                  fontSize: '1rem',
                                  verticalAlign: 'top',
                                  maxWidth: '160px',
                                }}
                              >
                                {entry ? (
                                  <div>
                                    <strong>{entry.hours}</strong>
                                    {notes && (
                                      <div style={{ marginTop: '0.35rem' }}>
                                        <ExpandableNotes notes={notes} compact />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                            );
                          })}
                          <td style={{ 
                            padding: '0.75rem', 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#e8f4f8',
                            fontSize: '1rem'
                          }}>
                            {calculateDailyTotal(date, matrix).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      {/* Weekly Total Row */}
                      <tr style={{ 
                        backgroundColor: '#d4edda',
                        borderTop: '2px solid #28a745',
                        borderBottom: '3px solid #28a745',
                        fontWeight: 'bold'
                      }}>
                        <td style={{ 
                          padding: '0.75rem',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: '#d4edda',
                          zIndex: 1,
                          fontStyle: 'italic'
                        }}>
                          Week Total
                        </td>
                        {rates.map((rate) => {
                          const weekRateTotal = weekDates.reduce((sum, date) => {
                            if (matrix[date] && matrix[date][rate.id]) {
                              return sum + parseFloat(matrix[date][rate.id].hours);
                            }
                            return sum;
                          }, 0);
                          return (
                            <td 
                              key={rate.id} 
                              style={{ 
                                padding: '0.75rem', 
                                textAlign: 'center',
                                color: weekRateTotal > 0 ? '#155724' : '#bdc3c7'
                              }}
                            >
                              {weekRateTotal > 0 ? weekRateTotal.toFixed(2) : '-'}
                            </td>
                          );
                        })}
                        <td style={{ 
                          padding: '0.75rem', 
                          textAlign: 'center',
                          backgroundColor: '#c3e6cb',
                          fontSize: '1.1rem'
                        }}>
                          {calculateWeeklyTotal(weekDates, matrix).toFixed(2)}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  {/* Rate totals row */}
                  <tr style={{ 
                    backgroundColor: '#34495e', 
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    <td style={{ 
                      padding: '0.75rem',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: '#34495e',
                      color: 'white',
                      zIndex: 1
                    }}>
                      Rate Total
                    </td>
                    {rates.map((rate) => (
                      <td key={rate.id} style={{ padding: '0.75rem', textAlign: 'center', color: 'white' }}>
                        {calculateRateTotal(rate.id, dates, matrix).toFixed(2)}
                      </td>
                    ))}
                    <td style={{ 
                      padding: '0.75rem', 
                      textAlign: 'center',
                      backgroundColor: '#2c3e50',
                      color: 'white',
                      fontSize: '1.1rem'
                    }}>
                      {parseFloat(timesheet.total_hours || '0').toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', borderTop: '1px solid #e9ecef', paddingTop: '1rem' }}>
          {timesheet.status === 'submitted' && onApprove && onReject && (
            <>
              <button
                onClick={() => {
                  onApprove(timesheet.id!);
                  onClose();
                }}
                className="btn btn-success"
                style={{ backgroundColor: '#27ae60' }}
              >
                Approve
              </button>
              <button
                onClick={() => {
                  onReject(timesheet.id!);
                  onClose();
                }}
                className="btn btn-danger"
              >
                Reject
              </button>
            </>
          )}
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetDetailModal;

